import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import OfferStrip from '../components/common/OfferStrip';
import FaqSection from '../components/common/FaqSection';
import AdSlot from '../components/advertisement/AdSlot';
import SubscriptionHeaderCard from '../components/subscription-status/SubscriptionHeaderCard';
import SubscriptionControls from '../components/subscription-status/SubscriptionControls';
import LiveBiddingTable, { CATEGORY_COLUMNS } from '../components/subscription-status/LiveBiddingTable';
import SubscriptionMetricsGuide from '../components/subscription-status/SubscriptionMetricsGuide';
import { SME_SUBSCRIPTION_FAQS, SME_SUBSCRIPTION_METRICS } from '../data/ipoSubscriptionData';
import {
  SME_SUBSCRIPTION_TABS,
  buildSubscriptionCsv,
  computeSubscriptionStats,
  filterSubscriptions,
  latestSync,
  subscriptionTabByKey,
  toSubscriptionStatusRow,
  upcomingSmeToStatusRow,
} from '../utils/ipoSubscription';
import { compareSubscriptionRows } from '../utils/subscriptionMapper';
import { fetchSmeSubscriptionSummary, selectSmeSubscriptionRows } from '../store/slices/subscriptionSlice';
import { fetchSmeUpcomingIpos, selectSmeOpeningSoonIpos } from '../store/slices/upcomingIposSlice';

const PAGE_SIZE = 16;
const DATA_MAX_AGE_MS = 60000;

// sNII / bNII / employee columns only appear when some SME issue reports them
const OPTIONAL_COLUMNS = ['snii', 'bnii', 'emp'];

const HEADINGS = {
  live: 'SME IPOs: Live Subscription Status (NSE Emerge)',
  closed: 'Closed SME IPOs: Final Subscription Status (NSE Emerge)',
};

const DESCRIPTION = (
  <>
    Live demand for SME IPOs during the bidding window on NSE Emerge, showing how many times each issue has been
    subscribed across <strong className="text-slate-700">Qualified Institutional Buyers (QIB)</strong>,{' '}
    <strong className="text-slate-700">Non-Institutional (NII)</strong> and{' '}
    <strong className="text-slate-700">Individual Investor</strong> categories. SME bids are placed in large lots, so
    multiples often run far higher than on the main board.
  </>
);

export const SmeSubscriptionPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = subscriptionTabByKey(searchParams.get('tab'), SME_SUBSCRIPTION_TABS).key;
  const [demand, setDemand] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  // GET /api/market/subscription/sme -- category-wise bids for SME issues that are open --
  // plus GET /api/market/ipos/sme/upcoming, which lists SME issues before bidding opens
  const subscriptionRows = useSelector(selectSmeSubscriptionRows);
  const { refreshedAt, status, error, note } = useSelector((state) => state.smeSubscription);
  const scheduledRows = useSelector(selectSmeOpeningSoonIpos);
  const scheduleStatus = useSelector((state) => state.smeUpcomingIpos.status);

  useEffect(() => {
    dispatch(fetchSmeSubscriptionSummary({ maxAgeMs: DATA_MAX_AGE_MS }));
    dispatch(fetchSmeUpcomingIpos({ maxAgeMs: DATA_MAX_AGE_MS }));
  }, [dispatch]);

  const SME_ISSUES = useMemo(() => {
    const bidding = subscriptionRows.map((row) => toSubscriptionStatusRow(row, refreshedAt));
    const withFigures = new Set(bidding.map((row) => row.symbol));
    // Scheduled issues without bidding figures yet: upcoming, or open before the first NSE update
    const scheduled = scheduledRows
      .filter((row) => !withFigures.has(row.symbol))
      .map((row) => upcomingSmeToStatusRow({ ...row, status: row.phase }));
    return [...bidding, ...scheduled].sort(compareSubscriptionRows);
  }, [subscriptionRows, refreshedAt, scheduledRows]);

  const columns = useMemo(
    () => CATEGORY_COLUMNS.filter((col) => !OPTIONAL_COLUMNS.includes(col.key) || SME_ISSUES.some((row) => typeof row[col.key] === 'number')),
    [SME_ISSUES]
  );

  const LAST_SYNC = useMemo(() => latestSync(SME_ISSUES), [SME_ISSUES]);
  const YEAR = LAST_SYNC ? LAST_SYNC.slice(0, 4) : String(new Date().getFullYear());
  const TAB_COUNTS = useMemo(
    () => Object.fromEntries(SME_SUBSCRIPTION_TABS.map((t) => [t.key, SME_ISSUES.filter(t.test).length])),
    [SME_ISSUES]
  );

  const pending = (feedStatus) => feedStatus === 'idle' || feedStatus === 'loading';
  const isLoading = !SME_ISSUES.length && (pending(status) || pending(scheduleStatus));
  const isFailed = !SME_ISSUES.length && status === 'failed' && scheduleStatus === 'failed';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADINGS[tab]} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, [tab]);

  useEffect(() => {
    setPage(1);
  }, [tab, demand, query]);

  const tabIssues = useMemo(() => SME_ISSUES.filter(subscriptionTabByKey(tab, SME_SUBSCRIPTION_TABS).test), [SME_ISSUES, tab]);
  const stats = useMemo(() => computeSubscriptionStats(tabIssues), [tabIssues]);
  const rows = useMemo(
    () => filterSubscriptions(SME_ISSUES, { tab, demand, query, tabs: SME_SUBSCRIPTION_TABS }),
    [SME_ISSUES, tab, demand, query]
  );

  const changeTab = (key) => setSearchParams(key === 'live' ? {} : { tab: key }, { replace: true });

  const handleExport = () => {
    // BOM so spreadsheet apps read the file as UTF-8
    const blob = new Blob(['﻿', buildSubscriptionCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sme-ipo-subscription-${tab}-${YEAR}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const resetFilters = () => {
    setDemand('all');
    setQuery('');
  };

  let emptyMessage;
  if (isLoading) {
    emptyMessage = 'Loading live SME subscription data…';
  } else if (isFailed) {
    emptyMessage = (
      <>
        SME subscription data is temporarily unavailable{error ? ` (${error})` : ''}.{' '}
        <button
          type="button"
          onClick={() => {
            dispatch(fetchSmeSubscriptionSummary());
            dispatch(fetchSmeUpcomingIpos());
          }}
          className="font-bold text-[#008080] hover:underline"
        >
          Retry
        </button>
      </>
    );
  } else if (!tabIssues.length) {
    emptyMessage =
      tab === 'closed' ? (
        'No closed SME issues in the current NSE feed.'
      ) : (
        <>
          {note || 'No SME IPOs are open or scheduled right now.'}{' '}
          <Link to="/sme-upcoming-ipos" className="font-bold text-[#008080] hover:underline">
            See upcoming SME IPOs
          </Link>
        </>
      );
  } else {
    emptyMessage = (
      <>
        No SME IPOs match these filters.{' '}
        <button type="button" onClick={resetFilters} className="font-bold text-[#008080] hover:underline">
          Show all
        </button>
      </>
    );
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        {/* Partner ad (GET /api/ads): page slot first, then the home leaderboard */}
        <OfferStrip slotId={['sme-ipo-subscription-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
            <Link to="/" className="hover:text-[#008080] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <Link to="/sme-ipos" className="hover:text-[#008080] transition-colors">
              SME IPOs
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-900 font-semibold">SME Subscription Status ({YEAR})</span>
          </nav>

          <SubscriptionHeaderCard
            heading={HEADINGS[tab]}
            stats={stats}
            lastSync={LAST_SYNC}
            year={YEAR}
            onExport={handleExport}
            canExport={rows.length > 0}
            description={DESCRIPTION}
            feedBadge="NSE Emerge SME Platform"
            totalLabel="Active SME Issues"
          />

          <SubscriptionControls
            tab={tab}
            onTabChange={changeTab}
            tabCounts={TAB_COUNTS}
            demand={demand}
            onDemandChange={setDemand}
            query={query}
            onQueryChange={setQuery}
            tabs={SME_SUBSCRIPTION_TABS}
            searchPlaceholder="Filter SME company or symbol…"
          />

          <LiveBiddingTable
            issues={rows}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            emptyMessage={emptyMessage}
            title="Live SME Bidding Multiples"
            badge="NSE Emerge"
            columns={columns}
          />

          <SubscriptionMetricsGuide title="Understanding SME Subscription Metrics & Categories" metrics={SME_SUBSCRIPTION_METRICS} />

          <FaqSection
            id="sme-subscription-faq"
            title="SME IPO Subscription FAQs"
            subtitle="How SME bidding figures work, and why lot sizes change what the multiples mean."
            faqs={SME_SUBSCRIPTION_FAQS}
          />

          {/* Partner ad (GET /api/ads): page slot first, then the home in-feed */}
          <AdSlot slotId={['sme-ipo-subscription-bottom', 'infeed']} variant="feature" />
        </div>
      </main>
    </>
  );
};

export default SmeSubscriptionPage;
